import { useState } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemText, Typography, CircularProgress, Alert, Tabs, Tab, Chip, Breadcrumbs, Link } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFunds, usePortfolioCompanies, useSubmissions, useUsersByPortfolioCompany, useMetricDefinitions } from '@lingaai/shared-hooks';
import type { Submission, User } from '@lingaai/types';

interface FundsTabProps {
  tenantId: string;
}

export function FundsTab({ tenantId }: FundsTabProps) {
  const [selectedFundId, setSelectedFundId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [fundTab, setFundTab] = useState(0); // 0 = Metric Defs, 1 = Portfolio Cos
  const [companyTab, setCompanyTab] = useState(0); // 0 = Submissions, 1 = Users

  const { data: funds, isLoading: fundsLoading } = useFunds(tenantId);
  const { data: companies, isLoading: companiesLoading } = usePortfolioCompanies(selectedFundId || '');
  const { data: metricDefinitions, isLoading: metricsLoading } = useMetricDefinitions(selectedFundId || '');
  const { data: submissions, isLoading: submissionsLoading } = useSubmissions(selectedCompanyId || undefined);
  const { data: users, isLoading: usersLoading } = useUsersByPortfolioCompany(selectedCompanyId || '');

  const submissionColumns: GridColDef<Submission>[] = [
    {
      field: 'period',
      headerName: 'Period',
      width: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
          draft: 'default',
          submitted: 'primary',
          processing: 'warning',
          completed: 'success',
          needs_review: 'error',
        };
        return <Chip label={params.value} size="small" color={statusColors[params.value as string] || 'default'} />;
      },
    },
    {
      field: 'source_type',
      headerName: 'Source',
      width: 120,
    },
    {
      field: 'submitted_at',
      headerName: 'Submitted',
      width: 150,
      valueFormatter: (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
  ];

  const userColumns: GridColDef<User>[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
    },
  ];

  const metricColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Metric Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'data_type',
      headerName: 'Type',
      width: 120,
    },
    {
      field: 'is_required',
      headerName: 'Required',
      width: 100,
      renderCell: (params: GridRenderCellParams) => <Chip label={params.value ? 'Yes' : 'No'} size="small" color={params.value ? 'primary' : 'default'} />,
    },
    {
      field: 'display_order',
      headerName: 'Order',
      width: 80,
    },
  ];

  if (fundsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!funds || funds.length === 0) {
    return <Alert severity="info">No funds found for this tenant.</Alert>;
  }

  const selectedFund = funds?.find((f) => f.id === selectedFundId);
  const selectedCompany = companies?.find((c) => c.id === selectedCompanyId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 600 }}>
      {/* Breadcrumbs */}
      {(selectedFundId || selectedCompanyId) && (
        <Breadcrumbs>
          <Link
            component="button"
            variant="body1"
            onClick={() => {
              setSelectedFundId(null);
              setSelectedCompanyId(null);
            }}
            sx={{ cursor: 'pointer' }}
          >
            All Funds
          </Link>
          {selectedFund && (
            <Link
              component="button"
              variant="body1"
              onClick={() => {
                setSelectedCompanyId(null);
                setFundTab(0); // Go to Portfolio Companies tab
              }}
              sx={{ cursor: 'pointer' }}
            >
              {selectedFund.name}
            </Link>
          )}
          {selectedCompany && <Typography color="text.primary">{selectedCompany.name}</Typography>}
        </Breadcrumbs>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
        {/* Funds List */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <Paper sx={{ height: '100%', overflow: 'auto' }}>
            <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              Funds
            </Typography>
            <List>
              {funds.map((fund) => (
                <ListItem key={fund.id} disablePadding>
                  <ListItemButton
                    selected={selectedFundId === fund.id}
                    onClick={() => {
                      setSelectedFundId(fund.id);
                      setSelectedCompanyId(null);
                      setFundTab(0);
                    }}
                  >
                    <ListItemText primary={fund.name} secondary={`${fund.reporting_frequency}`} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Fund Details with Tabs */}
        <Box sx={{ flex: 1 }}>
          {selectedFundId ? (
            selectedCompanyId ? (
              // Show company details when a company is selected
              <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={companyTab} onChange={(_, newValue) => setCompanyTab(newValue)}>
                    <Tab label="Submissions" />
                    <Tab label="Users" />
                  </Tabs>
                </Box>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {companyTab === 0 && (
                    <>
                      {submissionsLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <DataGrid
                          rows={submissions || []}
                          columns={submissionColumns}
                          pageSizeOptions={[25, 50, 100]}
                          slots={{
                            toolbar: GridToolbar,
                          }}
                          slotProps={{
                            toolbar: {
                              showQuickFilter: true,
                              quickFilterProps: { debounceMs: 500 },
                            },
                          }}
                          disableRowSelectionOnClick
                          sx={{ border: 0 }}
                        />
                      )}
                    </>
                  )}

                  {companyTab === 1 && (
                    <>
                      {usersLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <DataGrid
                          rows={users || []}
                          columns={userColumns}
                          pageSizeOptions={[25, 50, 100]}
                          slots={{
                            toolbar: GridToolbar,
                          }}
                          slotProps={{
                            toolbar: {
                              showQuickFilter: true,
                              quickFilterProps: { debounceMs: 500 },
                            },
                          }}
                          disableRowSelectionOnClick
                          sx={{ border: 0 }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            ) : (
              // Show fund tabs when fund is selected but no company
              <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={fundTab} onChange={(_, newValue) => setFundTab(newValue)}>
                    <Tab label="Portfolio Companies" />
                    <Tab label="Metric Definitions" />
                    <Tab label="Fund Settings" />
                  </Tabs>
                </Box>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {fundTab === 0 && (
                    <>
                      {companiesLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : companies && companies.length > 0 ? (
                        <List sx={{ overflow: 'auto' }}>
                          {companies.map((company) => (
                            <ListItem key={company.id} disablePadding>
                              <ListItemButton onClick={() => setSelectedCompanyId(company.id)}>
                                <ListItemText primary={company.name} secondary={company.sector || 'No sector'} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                          }}
                        >
                          <Typography color="text.secondary">No portfolio companies in this fund</Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {fundTab === 1 && (
                    <>
                      {metricsLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <DataGrid
                          rows={metricDefinitions || []}
                          columns={metricColumns}
                          pageSizeOptions={[25, 50, 100]}
                          slots={{
                            toolbar: GridToolbar,
                          }}
                          slotProps={{
                            toolbar: {
                              showQuickFilter: true,
                              quickFilterProps: { debounceMs: 500 },
                            },
                          }}
                          disableRowSelectionOnClick
                          sx={{ border: 0 }}
                        />
                      )}
                    </>
                  )}

                  {fundTab === 2 && (
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Fund Configuration
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Fund Name:</strong> {selectedFund?.name}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Reporting Frequency:</strong> {selectedFund?.reporting_frequency}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Reporting Day of Month:</strong> {selectedFund?.reporting_day_of_month}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Created:</strong> {selectedFund?.created_at ? new Date(selectedFund.created_at).toLocaleDateString() : '-'}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Updated:</strong> {selectedFund?.updated_at ? new Date(selectedFund.updated_at).toLocaleDateString() : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            )
          ) : (
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">Select a fund to view details</Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
